// User Management — Settings → Users tab.
//
// Tabs:
//   1. Active Users     — list, change role (super_admin only), deactivate (super_admin only)
//   2. Pending Invites  — list, resend, revoke
//
// RBAC enforcement (mirrored from backend, for UX only):
//   - Only super_admin sees role-change & deactivate controls
//   - Invite modal's "role" dropdown is filtered by canInviteRole()
//   - "Invite User" button hides for employee
//
// Backend is the source of truth: every privileged action posts to the API
// and the API re-checks the inviter's role. If the UI ever lies (e.g. someone
// hacks the DOM to enable a button), the backend will 403.

import { useEffect, useMemo, useState } from 'react';
import {
  UserPlus,
  Search,
  Loader2,
  Mail,
  Check,
  X,
  AlertCircle,
  RefreshCw,
  Trash2,
  UserCheck,
  UserX,
  Shield,
} from 'lucide-react';
import { api, ApiError } from '../../lib/api';
import { useAuth } from '../../lib/AuthContext';
import {
  canInviteRole,
  invitableRoles,
  isSuperAdmin,
  roleLabel,
} from '../../lib/permissions';

const ROLE_BADGE_STYLES = {
  super_admin: 'bg-[#162a52] text-white',
  admin: 'bg-[#c79a3a] text-white',
  employee: 'bg-slate-200 text-slate-800',
};

function RoleBadge({ role }) {
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${ROLE_BADGE_STYLES[role] || 'bg-slate-200 text-slate-800'}`}
    >
      {role === 'super_admin' && <Shield className="w-3 h-3" />}
      {roleLabel(role)}
    </span>
  );
}

function Toast({ kind, message, onClose }) {
  if (!message) return null;
  const styles =
    kind === 'error'
      ? 'bg-red-50 border-red-200 text-red-800'
      : 'bg-green-50 border-green-200 text-green-800';
  const Icon = kind === 'error' ? AlertCircle : Check;
  return (
    <div
      className={`fixed top-6 right-6 z-50 flex items-start gap-3 px-4 py-3 rounded-lg border shadow-lg max-w-md ${styles}`}
    >
      <Icon className="w-5 h-5 flex-shrink-0 mt-0.5" />
      <div className="flex-1 text-sm font-medium">{message}</div>
      <button
        onClick={onClose}
        className="text-current opacity-60 hover:opacity-100"
        aria-label="Dismiss"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}

function ConfirmDialog({ open, title, message, confirmLabel, danger, onConfirm, onCancel }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
        <h3 className="text-lg font-semibold text-[#162a52] mb-2">{title}</h3>
        <p className="text-sm text-slate-600 mb-6">{message}</p>
        <div className="flex justify-end gap-2">
          <button
            onClick={onCancel}
            className="px-4 py-2 rounded-md text-sm font-medium text-slate-700 hover:bg-slate-100"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className={`px-4 py-2 rounded-md text-sm font-medium text-white ${
              danger ? 'bg-red-600 hover:bg-red-700' : 'bg-[#162a52] hover:bg-[#1f3a6e]'
            }`}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

function InviteModal({ open, onClose, onSent, currentUser }) {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const allowed = useMemo(
    () => (currentUser ? invitableRoles(currentUser.role) : []),
    [currentUser],
  );

  useEffect(() => {
    if (open) {
      setEmail('');
      setRole(allowed[0] || '');
      setError(null);
    }
  }, [open, allowed]);

  if (!open) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await api.post('/invitations', { email: email.trim().toLowerCase(), role });
      onSent(`Invitation sent to ${email}`);
      onClose();
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : 'Failed to send invitation';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <h3 className="text-lg font-semibold text-[#162a52]">Invite User</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
            <X className="w-5 h-5" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Email address
            </label>
            <input
              type="email"
              required
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="person@example.com"
              className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#c79a3a]/40 focus:border-[#c79a3a]"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Role</label>
            <select
              required
              value={role}
              onChange={(e) => setRole(e.target.value)}
              className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#c79a3a]/40 focus:border-[#c79a3a]"
            >
              {allowed.map((r) => (
                <option key={r} value={r}>
                  {roleLabel(r)}
                </option>
              ))}
            </select>
            <p className="mt-1 text-xs text-slate-500">
              The invitee will receive an email with a link that expires in 72 hours.
            </p>
          </div>
          {error && (
            <div className="px-3 py-2 rounded bg-red-50 border border-red-200 text-sm text-red-800">
              {error}
            </div>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-md text-sm font-medium text-slate-700 hover:bg-slate-100"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting || !role}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium bg-[#162a52] text-white hover:bg-[#1f3a6e] disabled:opacity-60"
            >
              {submitting ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Mail className="w-4 h-4" />
              )}
              Send Invitation
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function UserManagement() {
  const { user: currentUser } = useAuth();
  const [tab, setTab] = useState('users');
  const [users, setUsers] = useState([]);
  const [employeesByUserId, setEmployeesByUserId] = useState({});
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [inviteOpen, setInviteOpen] = useState(false);
  const [toast, setToast] = useState({ kind: 'success', message: '' });
  const [confirm, setConfirm] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const showToast = (kind, message) => {
    setToast({ kind, message });
    setTimeout(() => setToast({ kind, message: '' }), 4000);
  };

  const refresh = async () => {
    setLoading(true);
    try {
      // Department and designation live on the employees table, not users.
      // Fetch alongside and match by userId — a user with no employee record
      // simply shows blank cells.
      const [u, i, e] = await Promise.all([
        api.get('/users'),
        api.get('/invitations'),
        api.get('/employees?active=all').catch(() => ({ items: [] })),
      ]);
      setEmployeesByUserId(
        Object.fromEntries((e.items || []).map((row) => [row.userId, row])),
      );
      setUsers(u.users || []);
      setInvites(i.invitations || []);
    } catch (err) {
      showToast('error', err instanceof ApiError ? err.message : 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredUsers = useMemo(() => {
    // Hide users whose linked employee was DELETED (archived: employees.active
    // === false). Settings-only deactivated users (employee still active) stay
    // listed so they can be reactivated here.
    const notDeleted = users.filter((u) => {
      // Never hide privileged users (super_admin / admin) — delete is a
      // sales-employee action. Only drop a user whose EMPLOYEE record is
      // archived AND who is a plain employee.
      if (u.role === "super_admin" || u.role === "admin") return true;
      const emp = employeesByUserId[u.id];
      return !(emp && emp.active === false);
    });
    const q = search.trim().toLowerCase();
    if (!q) return notDeleted;
    return notDeleted.filter(
      (u) =>
        u.email.toLowerCase().includes(q) ||
        (u.name && u.name.toLowerCase().includes(q)) ||
        roleLabel(u.role).toLowerCase().includes(q),
    );
  }, [users, search, employeesByUserId]);

  const handleChangeRole = async (target, newRole) => {
    setBusyId(target.id);
    try {
      await api.patch(`/users/${target.id}`, { role: newRole });
      showToast('success', `${target.email} is now ${roleLabel(newRole)}`);
      await refresh();
    } catch (err) {
      showToast('error', err instanceof ApiError ? err.message : 'Failed to change role');
    } finally {
      setBusyId(null);
    }
  };

  const handleToggleActive = async (target) => {
    setBusyId(target.id);
    try {
      await api.patch(`/users/${target.id}`, { active: !target.active });
      showToast(
        'success',
        `${target.email} ${target.active ? 'deactivated' : 'reactivated'}`,
      );
      await refresh();
    } catch (err) {
      showToast('error', err instanceof ApiError ? err.message : 'Failed to update user');
    } finally {
      setBusyId(null);
    }
  };

  const handleResendInvite = async (inv) => {
    setBusyId(inv.id);
    try {
      await api.post(`/invitations/${inv.id}/resend`, {});
      showToast('success', `Invitation re-sent to ${inv.email}`);
      await refresh();
    } catch (err) {
      showToast('error', err instanceof ApiError ? err.message : 'Failed to resend');
    } finally {
      setBusyId(null);
    }
  };

  const handleRevokeInvite = async (inv) => {
    setBusyId(inv.id);
    try {
      await api.delete(`/invitations/${inv.id}`);
      showToast('success', `Invitation to ${inv.email} revoked`);
      await refresh();
    } catch (err) {
      showToast('error', err instanceof ApiError ? err.message : 'Failed to revoke');
    } finally {
      setBusyId(null);
    }
  };

  const canInvite = currentUser && invitableRoles(currentUser.role).length > 0;

  return (
    <div className="space-y-6">
      <Toast
        kind={toast.kind}
        message={toast.message}
        onClose={() => setToast({ ...toast, message: '' })}
      />
      <ConfirmDialog
        open={!!confirm}
        title={confirm?.title}
        message={confirm?.message}
        confirmLabel={confirm?.confirmLabel}
        danger={confirm?.danger}
        onConfirm={() => {
          const fn = confirm.onConfirm;
          setConfirm(null);
          fn();
        }}
        onCancel={() => setConfirm(null)}
      />
      <InviteModal
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
        onSent={(msg) => {
          showToast('success', msg);
          refresh();
        }}
        currentUser={currentUser}
      />

      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-[#162a52]">Users & Access</h2>
          <p className="text-sm text-slate-500 mt-1">
            Manage team members, roles, and pending invitations.
          </p>
        </div>
        {canInvite && (
          <button
            onClick={() => setInviteOpen(true)}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-[#162a52] text-white text-sm font-medium hover:bg-[#1f3a6e]"
          >
            <UserPlus className="w-4 h-4" />
            Invite User
          </button>
        )}
      </div>

      <div className="border-b border-slate-200">
        <nav className="flex gap-6">
          <button
            onClick={() => setTab('users')}
            className={`pb-3 text-sm font-medium border-b-2 transition-colors ${
              tab === 'users'
                ? 'border-[#c79a3a] text-[#162a52]'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            Active Users
            <span className="ml-2 text-xs text-slate-400">({filteredUsers.length})</span>
          </button>
          <button
            onClick={() => setTab('invites')}
            className={`pb-3 text-sm font-medium border-b-2 transition-colors ${
              tab === 'invites'
                ? 'border-[#c79a3a] text-[#162a52]'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            Pending Invitations
            <span className="ml-2 text-xs text-slate-400">({invites.length})</span>
          </button>
        </nav>
      </div>

      {loading ? (
        <div className="py-20 flex items-center justify-center text-slate-400">
          <Loader2 className="w-6 h-6 animate-spin" />
        </div>
      ) : tab === 'users' ? (
        <div className="space-y-4">
          <div className="relative max-w-sm">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              placeholder="Search by name, email, or role…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#c79a3a]/40 focus:border-[#c79a3a]"
            />
          </div>
          <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
            <div className="overflow-auto max-h-[70vh] -mx-3 sm:mx-0"><table className="w-full min-w-[880px]">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr className="text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                  <th className="px-4 py-3">Name</th>
                  <th className="px-4 py-3">Email</th>
                  {/* Department and designation come from the employees table,
                      matched by userId. Placed with name/email because they
                      describe the person, not their access level. */}
                  <th className="px-4 py-3">Department</th>
                  <th className="px-4 py-3">Designation</th>
                  <th className="px-4 py-3">Role</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredUsers.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-4 py-12 text-center text-sm text-slate-400">
                      No users found.
                    </td>
                  </tr>
                )}
                {filteredUsers.map((u) => {
                  const isSelf = currentUser && u.id === currentUser.id;
                  const showAdminControls = currentUser && isSuperAdmin(currentUser.role);
                  return (
                    <tr key={u.id} className="hover:bg-slate-50">
                      <td className="px-4 py-3 text-sm font-medium text-[#162a52]">
                        {u.name || <span className="text-slate-400">—</span>}
                        {isSelf && (
                          <span className="ml-2 text-xs text-slate-400">(you)</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-700">{u.email}</td>
                      <td className="px-4 py-3 text-sm text-slate-700">
                        {employeesByUserId[u.id]?.department || <span className="text-slate-400">&mdash;</span>}
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-700">
                        {employeesByUserId[u.id]?.designation || <span className="text-slate-400">&mdash;</span>}
                      </td>
                      <td className="px-4 py-3">
                        {showAdminControls && !isSelf ? (
                          <select
                            value={u.role}
                            disabled={busyId === u.id}
                            onChange={(e) => {
                              const newRole = e.target.value;
                              if (newRole === u.role) return;
                              setConfirm({
                                title: 'Change role?',
                                message: `Change ${u.email}'s role from ${roleLabel(u.role)} to ${roleLabel(newRole)}? Their active sessions will be revoked and they will need to sign in again.`,
                                confirmLabel: 'Change role',
                                onConfirm: () => handleChangeRole(u, newRole),
                              });
                            }}
                            className="text-xs px-2 py-1 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#c79a3a]/40"
                          >
                            {/* Super Admin renders ONLY when it is already this
                                user's role, so their row displays correctly.
                                It is never offered as a promotion target — a
                                second super_admin should be a deliberate act,
                                not a two-click dropdown change. Demotion of an
                                existing super_admin stays possible. */}
                            {u.role === 'super_admin' && (
                              <option value="super_admin">Super Admin</option>
                            )}
                            <option value="admin">Admin</option>
                            <option value="employee">Employee</option>
                          </select>
                        ) : (
                          <RoleBadge role={u.role} />
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {u.active ? (
                          <span className="inline-flex items-center gap-1 text-xs font-medium text-green-700">
                            <UserCheck className="w-3.5 h-3.5" /> Active
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-xs font-medium text-slate-500">
                            <UserX className="w-3.5 h-3.5" /> Inactive
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        {showAdminControls && !isSelf && (
                          <button
                            onClick={() => {
                              setConfirm({
                                title: u.active ? 'Deactivate user?' : 'Reactivate user?',
                                message: u.active
                                  ? `${u.email} will be signed out immediately and unable to sign back in.`
                                  : `${u.email} will be able to sign in again.`,
                                confirmLabel: u.active ? 'Deactivate' : 'Reactivate',
                                danger: u.active,
                                onConfirm: () => handleToggleActive(u),
                              });
                            }}
                            disabled={busyId === u.id}
                            className="text-xs font-medium text-slate-600 hover:text-[#162a52] disabled:opacity-50"
                          >
                            {busyId === u.id ? (
                              <Loader2 className="w-3.5 h-3.5 animate-spin inline" />
                            ) : u.active ? (
                              'Deactivate'
                            ) : (
                              'Reactivate'
                            )}
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table></div>
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
          <div className="overflow-auto max-h-[70vh] -mx-3 sm:mx-0"><table className="w-full min-w-[640px]">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                <th className="px-4 py-3">Email</th>
                <th className="px-4 py-3">Role</th>
                <th className="px-4 py-3">Invited By</th>
                <th className="px-4 py-3">Expires</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {invites.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-4 py-12 text-center text-sm text-slate-400">
                    No pending invitations.
                  </td>
                </tr>
              )}
              {invites.map((inv) => {
                const expiresAt = new Date(inv.expiresAt);
                const expired = expiresAt < new Date();
                const inviterAllowed =
                  currentUser && canInviteRole(currentUser.role, inv.role);
                return (
                  <tr key={inv.id} className="hover:bg-slate-50">
                    <td className="px-4 py-3 text-sm font-medium text-[#162a52]">
                      {inv.email}
                    </td>
                    <td className="px-4 py-3">
                      <RoleBadge role={inv.role} />
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-700">
                      {inv.inviterName || inv.inviterEmail || '—'}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {expired ? (
                        <span className="text-red-600 font-medium">Expired</span>
                      ) : (
                        <span className="text-slate-600">
                          {expiresAt.toLocaleString()}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex justify-end gap-2">
                        {inviterAllowed && (
                          <button
                            onClick={() => handleResendInvite(inv)}
                            disabled={busyId === inv.id}
                            className="inline-flex items-center gap-1 text-xs font-medium text-slate-600 hover:text-[#162a52] disabled:opacity-50"
                          >
                            {busyId === inv.id ? (
                              <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            ) : (
                              <RefreshCw className="w-3.5 h-3.5" />
                            )}
                            Resend
                          </button>
                        )}
                        {inviterAllowed && (
                          <button
                            onClick={() =>
                              setConfirm({
                                title: 'Revoke invitation?',
                                message: `The invitation link sent to ${inv.email} will stop working immediately.`,
                                confirmLabel: 'Revoke',
                                danger: true,
                                onConfirm: () => handleRevokeInvite(inv),
                              })
                            }
                            disabled={busyId === inv.id}
                            className="inline-flex items-center gap-1 text-xs font-medium text-red-600 hover:text-red-700 disabled:opacity-50"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                            Revoke
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table></div>
        </div>
      )}
    </div>
  );
}
